import { useId, useState } from 'react';
import {
  ChartFrame,
  EmptyChart,
  HatchPattern,
  INK,
  Legend,
  MotionStyles,
  XAxis,
  YAxis,
  moneyText,
  seriesColour,
  signedText,
  tickColumnWidth,
  useMeasuredWidth,
  usePrefersReducedMotion,
  type ChartTable,
  type TooltipRow,
  type TooltipState,
} from './Chart';
import { bandScale, linePath, linearScale, monthLabel, niceDomain, niceTicks, spansYears, stackExtent, stackSegments } from './layout';

/**
 * Columns by month, each a stack of series, with one or more columns per
 * month side by side and an optional line over the top.
 *
 * ── WHAT IT IS FOR ──
 * "Money in and out, month by month": one column of income and one of
 * expense per month, each stacked by book in the book's colour, with the
 * net profit drawn as a line across the same axis. Income and expense are
 * both money on the same scale, which is what lets the line share the axis -
 * the one-axis rule holds because there is only one unit.
 *
 * ── COLOUR IS THE BOOK; TEXTURE IS THE DIRECTION ──
 * A book has one colour, and it keeps that colour on both columns. Money
 * OUT is hatched. So the two facts a column carries - whose money and which
 * way it went - travel on two channels, and neither leans on the other: in a
 * greyscale print the hatch still says "out", and a person who cannot tell
 * the blue from the teal still has the legend, the `<title>` and the table.
 *
 * ── ONE TOOLTIP, EVERY SERIES ──
 * The pointer picks a MONTH, not a bar: aiming at a sliver 6px tall is not
 * something a finger can do. Whichever month is nearest, the readout lists
 * every book's in and out for it and the line's value. A tap does the same
 * and stays until the next tap; the arrow keys walk the months.
 */

export interface BarSeries {
  id: string;
  name: string;
  /** A palette token, or null for the theme's primary. */
  colour: string | null;
  /** One integer of minor units per month, in the order of `months`. */
  values: number[];
}

export interface BarGroup {
  id: string;
  /** For the legend: "Money in". */
  name: string;
  /** For the tooltip and the table: "in". */
  short: string;
  hatch?: boolean;
  series: BarSeries[];
}

export interface StackedBarsProps {
  months: string[];
  groups: BarGroup[];
  line?: { name: string; colour: string | null; values: number[] };
  currency: string;
  /** The chart's name, for the keyboard group and the table caption. */
  title: string;
  /** One sentence for the picture's `aria-label`. */
  summary: string;
  empty: { title: string; description?: string };
  height?: number;
  /**
   * More for the readout of a month, after the series: a comparison, a
   * count, what a click does. The chart knows its bars; the page knows
   * what else is true about the month.
   */
  extraRows?: (monthIndex: number) => TooltipRow[];
  /** A click on a month (or Enter on the stepped-to one). The cursor says so. */
  onSelect?: (monthIndex: number) => void;
  /** The group names in the legend - off when there is one group and its name would only repeat the title. */
  groupLegend?: boolean;
}

/** `left` is not here: the tick column is measured from its labels - see `tickColumnWidth`. */
const MARGIN = { top: 14, right: 12, bottom: 26 };
const MAX_BAR = 24;
const GAP = 3;

export function StackedBars({ months, groups, line, currency, title, summary, empty, height = 260, extraRows, onSelect, groupLegend = true }: StackedBarsProps) {
  const [frameRef, width] = useMeasuredWidth<HTMLDivElement>();
  const reduced = usePrefersReducedMotion();
  const patternPrefix = useId();
  const [active, setActive] = useState<number | null>(null);

  const moved =
    groups.some((group) => group.series.some((series) => series.values.some((value) => value !== 0))) ||
    (line?.values.some((value) => value !== 0) ?? false);
  if (months.length === 0 || !moved) return <EmptyChart title={empty.title} description={empty.description} />;

  // ── the scales ──
  let lo = 0;
  let hi = 0;
  months.forEach((_, i) => {
    for (const group of groups) {
      const [min, max] = stackExtent(group.series.map((series) => series.values[i] ?? 0));
      lo = Math.min(lo, min);
      hi = Math.max(hi, max);
    }
    const value = line?.values[i];
    if (value !== undefined) {
      lo = Math.min(lo, value);
      hi = Math.max(hi, value);
    }
  });
  const domain = niceDomain(lo, hi);
  const ticks = niceTicks(lo, hi);
  const plotLeft = tickColumnWidth(ticks, currency);
  const plotRight = Math.max(plotLeft + 40, width - MARGIN.right);
  const plotTop = MARGIN.top;
  const plotBottom = height - MARGIN.bottom;
  const y = linearScale(domain, [plotBottom, plotTop]);
  // Less air between months when a year has to fit a phone: at 25px a
  // slot, a third of it empty left two 6px columns.
  const crowded = (plotRight - plotLeft) / months.length < 40;
  const band = bandScale(months.length, plotLeft, plotRight, crowded ? 0.2 : 0.35);
  const columns = Math.max(1, groups.length);
  const bar = Math.min(MAX_BAR, (band.band - GAP * (columns - 1)) / columns);
  const cluster = bar * columns + GAP * (columns - 1);
  const clusterLeft = (i: number) => band.x(i) + (band.band - cluster) / 2;
  const centre = (i: number) => clusterLeft(i) + cluster / 2;
  const withYear = spansYears(months);

  // ── the readout ──
  const tooltipFor = (i: number): TooltipState => {
    const rows: TooltipRow[] = [];
    let top = plotBottom;
    for (const group of groups) {
      const values = group.series.map((series) => series.values[i] ?? 0);
      top = Math.min(top, y(stackExtent(values)[1]));
      group.series.forEach((series, s) => {
        rows.push({
          key: `${group.id}:${series.id}`,
          colour: series.colour,
          kind: group.hatch ? 'hatch' : 'rect',
          label: series.name ? `${series.name} ${group.short}` : group.name,
          value: moneyText(values[s] ?? 0, currency),
        });
      });
    }
    if (line) {
      const value = line.values[i] ?? 0;
      top = Math.min(top, y(value));
      rows.push({ key: 'line', colour: line.colour, kind: 'line', label: line.name, value: signedText(value, currency) });
    }
    if (extraRows) rows.push(...extraRows(i));
    return { x: centre(i), y: top, title: monthLabel(months[i] ?? '', 'long'), rows };
  };

  const step = (direction: -1 | 1 | 'first' | 'last') => {
    setActive((current) => {
      if (direction === 'first') return 0;
      if (direction === 'last') return months.length - 1;
      const next = (current ?? (direction === 1 ? -1 : months.length)) + direction;
      return Math.max(0, Math.min(months.length - 1, next));
    });
  };

  const pointerMonth = (event: { currentTarget: SVGSVGElement; clientX: number }) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return band.index(event.clientX - rect.left);
  };

  // ── the table ──
  const table: ChartTable = {
    caption: `${title}, in ${currency}`,
    columns: [
      'Month',
      ...groups.flatMap((group) => group.series.map((series) => (series.name ? `${series.name} ${group.short}` : group.name))),
      ...(line ? [line.name] : []),
    ],
    rows: months.map((month, i) => ({
      key: month,
      cells: [
        monthLabel(month, 'long'),
        ...groups.flatMap((group) => group.series.map((series) => moneyText(series.values[i] ?? 0, currency))),
        ...(line ? [signedText(line.values[i] ?? 0, currency)] : []),
      ],
    })),
  };

  const legend = [
    // A series with no name (one book, drawn in its own colour) has no legend row of its own; the groups say in and out.
    ...(groups[0]?.series.filter((series) => series.name).map((series) => ({ key: `s:${series.id}`, label: series.name, colour: series.colour, kind: 'rect' as const })) ?? []),
    ...(groupLegend ? groups.map((group) => ({ key: `g:${group.id}`, label: group.name, colour: 'slate', kind: group.hatch ? ('hatch' as const) : ('rect' as const) })) : []),
    ...(line ? [{ key: 'line', label: line.name, colour: line.colour, kind: 'line' as const }] : []),
  ];

  const linePoints = line ? months.map((_, i) => ({ x: centre(i), y: y(line.values[i] ?? 0) })) : [];

  return (
    <div className="space-y-2">
      <ChartFrame
        title={title}
        table={table}
        tooltip={active === null ? null : tooltipFor(active)}
        onStep={step}
        onLeave={() => setActive(null)}
        onSelect={onSelect ? () => { if (active !== null) onSelect(active); } : undefined}
        frameRef={frameRef}
      >
        <svg
          role="img"
          aria-label={summary}
          width="100%"
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          className={onSelect ? 'block cursor-pointer select-none' : 'block select-none'}
          style={{ touchAction: 'pan-y' }}
          onPointerMove={(event) => {
            if (event.pointerType === 'touch') return;
            setActive(pointerMonth(event));
          }}
          onPointerDown={(event) => {
            if (event.pointerType !== 'touch') return;
            const month = pointerMonth(event);
            // A second tap on the month whose readout is open is the click.
            if (onSelect && active === month) {
              onSelect(month);
              return;
            }
            setActive((current) => (current === month ? null : month));
          }}
          onClick={(event) => {
            if (!onSelect) return;
            // Touch selects on its second tap (above); a mouse click opens straight away.
            if (matchMedia('(pointer: coarse)').matches) return;
            onSelect(pointerMonth(event));
          }}
          onPointerLeave={(event) => {
            if (event.pointerType !== 'touch') setActive(null);
          }}
        >
          <defs>
            {groups
              .filter((group) => group.hatch)
              .flatMap((group) =>
                group.series.map((series) => (
                  <HatchPattern key={`${group.id}:${series.id}`} id={`${patternPrefix}${group.id}-${series.id}`} colour={series.colour} />
                )),
              )}
            <MotionStyles enabled={!reduced} />
          </defs>

          <YAxis ticks={ticks} y={y} left={plotLeft} right={plotRight} currency={currency} />

          {active !== null ? (
            <rect
              aria-hidden
              x={band.x(active) - (band.step - band.band) / 2}
              y={plotTop}
              width={band.step}
              height={plotBottom - plotTop}
              fill="hsl(var(--foreground) / 0.05)"
            />
          ) : null}

          {months.map((month, i) =>
            groups.map((group, g) => {
              const values = group.series.map((series) => series.values[i] ?? 0);
              const segments = stackSegments(values);
              const x = clusterLeft(i) + g * (bar + GAP);
              return group.series.map((series, s) => {
                const segment = segments[s];
                if (!segment || segment.start === segment.end) return null;
                const top = y(Math.max(segment.start, segment.end));
                const bottom = y(Math.min(segment.start, segment.end));
                const fill = group.hatch ? `url(#${patternPrefix}${group.id}-${series.id})` : seriesColour(series.colour);
                return (
                  <rect
                    key={`${month}:${group.id}:${series.id}`}
                    x={x}
                    y={top}
                    width={bar}
                    height={Math.max(0.5, bottom - top)}
                    fill={fill}
                    stroke={INK.surface}
                    strokeWidth={1}
                    style={
                      reduced
                        ? undefined
                        : { transformOrigin: `${x}px ${y(0)}px`, animation: 'chart-grow 450ms ease-out both' }
                    }
                  >
                    <title>{`${monthLabel(month, 'long')} · ${series.name} ${group.short}: ${moneyText(values[s] ?? 0, currency)}`}</title>
                  </rect>
                );
              });
            }),
          )}

          {line ? (
            <g aria-hidden>
              <path
                d={linePath(linePoints)}
                fill="none"
                stroke={seriesColour(line.colour)}
                strokeWidth={2}
                strokeLinejoin="round"
                strokeLinecap="round"
                pathLength={1}
                style={reduced ? undefined : { strokeDasharray: 1, animation: 'chart-draw 600ms ease-out both' }}
              />
              {linePoints.map((point, i) => (
                <circle
                  key={months[i]}
                  cx={point.x}
                  cy={point.y}
                  r={active === i ? 5 : 3.5}
                  fill={seriesColour(line.colour)}
                  stroke={INK.surface}
                  strokeWidth={2}
                >
                  <title>{`${monthLabel(months[i] ?? '', 'long')} · ${line.name}: ${signedText(line.values[i] ?? 0, currency)}`}</title>
                </circle>
              ))}
            </g>
          ) : null}

          <XAxis labels={months.map((month) => monthLabel(month, 'short', withYear))} centre={centre} y={height - 8} band={band.step} />
        </svg>
      </ChartFrame>
      <Legend items={legend} />
    </div>
  );
}
